import React, { useState, useRef } from 'react';
import {
  Droplets, Eye, EyeOff, Lock, User, AlertCircle,
  Shield, Users, Phone, ArrowLeft, CheckCircle
} from 'lucide-react';
import { signIn, requestPasswordReset, confirmPasswordReset } from '../cognito';
import './SignIn.css';
import { getManagerByEmail, getCEByEmail } from '../supportConfig';

// ── Hardcoded team credentials (UI-only, no Cognito needed) ──────────────────
const TEAM_ACCOUNTS = [
  // Demo admin accounts (bypass Cognito for demo site)
  { email:'ayeshakhan@hoags.in',    password:'Demo@1234',  role:'admin',           name:'Ayesha Khan',    region:'Chennai'       },
  { email:'hari.babu@havells.com',  password:'Havells@2026', role:'admin',         name:'Hari Babu',      region:'All'           },
  { email:'aman@hoags.in',           password:'Demo@1234',  role:'service_manager', name:'Aman Sharma',    region:'Delhi'         },
  // Service Managers
  { email:'ayeshakhan@hoags.in',    password:'Hoags@1234', role:'service_manager', name:'Ayesha Khan',    region:'Chennai'       },
  { email:'Aman@hoags.in',           password:'Hoags@1234', role:'service_manager', name:'Aman Sodhi',     region:'Delhi'         },
  { email:'ravi.shankar@hoags.in',   password:'Hoags@1234', role:'service_manager', name:'Ravi Shankar',   region:'Mumbai'        },
  { email:'pradeep.kumar@hoags.in',  password:'Hoags@1234', role:'service_manager', name:'Pradeep Kumar',  region:'Hyderabad'     },
  { email:'divya.menon@hoags.in',    password:'Hoags@1234', role:'service_manager', name:'Divya Menon',    region:'Karnataka'     },
  // Customer Engineers
  { email:'suresh.ce@hoags.in',  password:'Hoags@1234', role:'customer_engineer', name:'Suresh Kumar', region:'Chennai' },
  { email:'priya.ce@hoags.in',   password:'Hoags@1234', role:'customer_engineer', name:'Priya Devi',   region:'Chennai' },
  { email:'rahul.ce@hoags.in',   password:'Hoags@1234', role:'customer_engineer', name:'Rahul Sharma', region:'Delhi'   },
];

function findTeamAccount(username, password) {
  const u = username.trim().toLowerCase();
  const p = password.trim();
  return TEAM_ACCOUNTS.find(a => a.email.toLowerCase() === u && a.password === p) || null;
}

const OTP_API = 'https://diag.hoags.in/waas/authentication/otp';
const STEP_PHONE = 'phone';
const STEP_OTP   = 'otp';
const STEP_CREDS = 'creds';

export default function SignIn({ onSignedIn }) {
  const [role,      setRole]      = useState('admin');
  const [step,      setStep]      = useState(STEP_CREDS);
  const [phone,     setPhone]     = useState('');
  const [otp,       setOtp]       = useState(['','','','']);
  const [serverOtp, setServerOtp] = useState('');
  const [username,  setUsername]  = useState('');
  const [password,  setPassword]  = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [otpSent,   setOtpSent]   = useState(false);
  const otpRefs = useRef([]);

  // ── Forgot password state ──
  const [forgotStep,    setForgotStep]    = useState(null); // null | 'email' | 'code' | 'done'
  const [forgotEmail,   setForgotEmail]   = useState('');
  const [forgotCode,    setForgotCode]    = useState('');
  const [forgotNewPw,   setForgotNewPw]   = useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = useState('');
  const [forgotPool,    setForgotPool]    = useState('admin');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError,   setForgotError]   = useState('');
  const [showForgotPw,  setShowForgotPw]  = useState(false);

  const isAdmin = role === 'admin';
  const accentColor    = '#DC2626';
  const accentGradient = 'linear-gradient(135deg,#B91C1C,#DC2626)';

  const handleRoleSwitch = (r) => {
    setRole(r);
    setStep(r === 'admin' ? STEP_CREDS : STEP_PHONE);
    setError(''); setPhone(''); setOtp(['','','','']);
    setUsername(''); setPassword(''); setOtpSent(false); setServerOtp('');
  };

  const TEST_PHONE = '1234567898';
  const TEST_OTP   = '1234';

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const cleaned = phone.trim().replace(/\D/g,'');
    if (!cleaned) { setError('Please enter your phone number.'); return; }
    if (cleaned.length !== 10) { setError('Please enter a valid 10-digit mobile number.'); return; }
    const formatted = `+91${cleaned}`;
    setError(''); setLoading(true);
    if (cleaned === TEST_PHONE) {
      setServerOtp(TEST_OTP); setOtpSent(true); setStep(STEP_OTP); setLoading(false);
      setTimeout(() => otpRefs.current[0]?.focus(), 100); return;
    }
    try {
      const res = await fetch(OTP_API, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({phone:formatted}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      setServerOtp(String(data.otp||data.OTP||data.code||''));
      setOtpSent(true); setStep(STEP_OTP);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) { setError(err.message||'Failed to send OTP. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    const entered = otp.join('');
    if (entered.length < 4) { setError('Please enter the 4-digit OTP.'); return; }
    if (serverOtp && entered !== serverOtp) { setError('Incorrect OTP. Please try again.'); return; }
    setError(''); setStep(STEP_CREDS);
  };

  const handleOtpChange = (val, idx) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp]; next[idx] = val; setOtp(next);
    if (val && idx < 3) otpRefs.current[idx + 1]?.focus();
  };
  const handleOtpKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) otpRefs.current[idx-1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,4);
    if (pasted.length === 4) { setOtp(pasted.split('')); otpRefs.current[3]?.focus(); }
    e.preventDefault();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!username.trim()||!password.trim()) { setError('Please enter your username and password.'); return; }
    setError(''); setLoading(true);
    try {
      // Check hardcoded team accounts first (SM + CE — no Cognito needed)
      const teamAccount = findTeamAccount(username, password);
      if (teamAccount) {
        // Create a mock session object with email in payload
        const mockSession = {
          getIdToken: () => ({
            payload: { email: teamAccount.email, name: teamAccount.name, sub: teamAccount.email },
            getJwtToken: () => 'mock-token',
          }),
          getAccessToken: () => ({ getJwtToken: () => 'mock-token' }),
          _teamAccount: teamAccount,
        };
        onSignedIn(mockSession, teamAccount.role);
        return;
      }
      // Fall through to Cognito for admin
      const { session, role: resolvedRole } = await signIn(username.trim(), password, role);
      onSignedIn(session, resolvedRole || role);
    } catch (err) {
      if (err.code==='NewPasswordRequired') setError('A new password is required. Please contact your administrator.');
      else if (err.code==='NotAuthorizedException') setError('Incorrect username or password.');
      else if (err.code==='UserNotFoundException') setError('User not found. Please check your username.');
      else setError(err.message||'Sign in failed. Please try again.');
    } finally { setLoading(false); }
  };

  const ErrorBox = () => error ? (
    <div className="signin-error"><AlertCircle size={15}/><span>{error}</span></div>
  ) : null;

  // ── Forgot password handlers ──
  const openForgotPassword = () => {
    setForgotEmail(username.trim());
    setForgotCode(''); setForgotNewPw(''); setForgotConfirmPw('');
    setForgotError(''); setForgotStep('email');
  };

  const closeForgotPassword = () => {
    setForgotStep(null); setForgotError('');
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim() || !forgotEmail.includes('@')) {
      setForgotError('Enter a valid email address.'); return;
    }
    setForgotError(''); setForgotLoading(true);
    try {
      const { pool } = await requestPasswordReset(forgotEmail.trim());
      setForgotPool(pool);
      setForgotStep('code');
    } catch (err) {
      if (err.code === 'UserNotFoundException') {
        setForgotError("No account found with this email. If you're a Customer Engineer, please contact your manager to reset your password.");
      } else if (err.code === 'LimitExceededException') {
        setForgotError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setForgotError(err.message || 'Failed to send reset code. Please try again.');
      }
    } finally { setForgotLoading(false); }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    if (!forgotCode.trim()) { setForgotError('Enter the code sent to your email.'); return; }
    if (forgotNewPw.length < 8) { setForgotError('Password must be at least 8 characters.'); return; }
    if (forgotNewPw !== forgotConfirmPw) { setForgotError('Passwords do not match.'); return; }
    setForgotError(''); setForgotLoading(true);
    try {
      await confirmPasswordReset(forgotEmail.trim(), forgotCode.trim(), forgotNewPw, forgotPool);
      setForgotStep('done');
      setUsername(forgotEmail.trim());
    } catch (err) {
      if (err.code === 'CodeMismatchException') setForgotError('Incorrect code. Please check your email and try again.');
      else if (err.code === 'ExpiredCodeException') setForgotError('This code has expired. Please request a new one.');
      else if (err.code === 'InvalidPasswordException') setForgotError('Password does not meet policy: min 8 chars, upper + lower + digit + special.');
      else setForgotError(err.message || 'Failed to reset password. Please try again.');
    } finally { setForgotLoading(false); }
  };

  const BackBtn = ({ to, label }) => (
    <button type="button" onClick={() => { setStep(to); setError(''); }}
      style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'none',cursor:'pointer',color:'#8E8E93',fontSize:12,fontWeight:500,marginBottom:14,padding:0,fontFamily:'inherit'}}>
      <ArrowLeft size={13}/> {label}
    </button>
  );

  return (
    <div className="signin-bg">
      <div className="water-particles">
        {[{l:'8%',w:5,h:12,dur:'4.2s',delay:'0s'},{l:'18%',w:4,h:10,dur:'6.1s',delay:'1.4s'},{l:'29%',w:6,h:14,dur:'5.3s',delay:'0.7s'},{l:'41%',w:3,h:8,dur:'7.0s',delay:'2.1s'},{l:'54%',w:5,h:12,dur:'4.8s',delay:'3.5s'},{l:'63%',w:4,h:10,dur:'5.9s',delay:'0.3s'},{l:'74%',w:7,h:16,dur:'6.4s',delay:'1.8s'},{l:'83%',w:3,h:8,dur:'4.5s',delay:'2.9s'},{l:'91%',w:5,h:12,dur:'7.2s',delay:'0.9s'}].map((d,i)=>(
          <div key={i} className="wdrop" style={{left:d.l,width:d.w,height:d.h,animationDuration:d.dur,animationDelay:d.delay}}/>
        ))}
        {[{l:'12%',s:8,dur:'8s',delay:'0s'},{l:'25%',s:5,dur:'11s',delay:'2s'},{l:'38%',s:10,dur:'9s',delay:'4s'},{l:'52%',s:6,dur:'13s',delay:'1s'},{l:'67%',s:4,dur:'7s',delay:'3s'},{l:'78%',s:9,dur:'10s',delay:'5s'},{l:'88%',s:5,dur:'12s',delay:'2.5s'}].map((b,i)=>(
          <div key={i} className="wbubble" style={{left:b.l,width:b.s,height:b.s,animationDuration:b.dur,animationDelay:b.delay}}/>
        ))}
        {[{l:'20%',b:'5%',s:20,dur:'3s',delay:'0s'},{l:'50%',b:'3%',s:16,dur:'4s',delay:'1.5s'},{l:'78%',b:'6%',s:24,dur:'3.5s',delay:'0.8s'}].map((r,i)=>(
          <div key={i} className="wripple" style={{left:r.l,bottom:r.b,width:r.s,height:r.s/2.5,animationDuration:r.dur,animationDelay:r.delay}}/>
        ))}
      </div>

      <div className="signin-waves">
        <svg className="signin-wave-row" style={{animationDuration:'8s'}} viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,40 C180,80 360,0 540,40 C720,80 900,0 1080,40 C1260,80 1440,0 1440,40 L1440,80 L0,80 Z" fill="rgba(14,165,233,0.07)"/>
        </svg>
        <svg className="signin-wave-row" style={{animationDuration:'12s',bottom:0,opacity:.6}} viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,30 C200,60 400,0 600,30 C800,60 1000,0 1200,30 C1350,50 1440,20 1440,30 L1440,60 L0,60 Z" fill="rgba(56,189,248,0.05)"/>
        </svg>
      </div>

      <svg className="signin-purifier-bg" width="160" height="220" viewBox="0 0 46 66" fill="none">
        <rect x="8" y="2" width="30" height="58" rx="5" fill="white"/>
        <rect x="10" y="4" width="26" height="14" rx="3" fill="white"/>
        <rect x="18" y="59" width="10" height="5" rx="2" fill="white"/>
      </svg>

      <div className="signin-card">
        {/* Logo */}
        <div className="signin-logo" style={{background: accentGradient}}>
          <div className="signin-logo-icon" style={{background:'#fff', borderRadius:18, overflow:'hidden', padding:14, display:'flex', alignItems:'center', justifyContent:'center', width:100, height:100}}>
            <img src="/havells-logo.png" alt="Havells" style={{width:72, height:72, objectFit:'contain'}}/>
          </div>
          <h1>{isAdmin ? 'Havells Waas' : 'Havells Purifier'}</h1>
          <p>{isAdmin ? 'Havells Device Management Panel' : 'Smart Water Purifier Portal'}</p>
          <svg className="signin-logo-wave" viewBox="0 0 800 28" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,14 C100,28 200,0 300,14 C400,28 500,0 600,14 C700,28 800,0 800,14 L800,28 L0,28 Z" fill="rgba(255,255,255,0.12)"/>
            <path d="M0,18 C130,8 260,28 400,18 C540,8 670,28 800,18 L800,28 L0,28 Z" fill="rgba(255,255,255,0.08)"/>
          </svg>
        </div>

        {/* Admin badge */}
        <div style={{padding:'20px 36px 0'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'8px 16px',borderRadius:10,background:'rgba(220,38,38,.1)',border:'1px solid rgba(220,38,38,.2)'}}>
            <Shield size={14} color="#DC2626"/>
            <span style={{fontSize:12,fontWeight:700,color:'#DC2626',letterSpacing:.3}}>ADMINISTRATOR ACCESS</span>
          </div>
        </div>

        {/* Phone step */}
        {!isAdmin && step === STEP_PHONE && (
          <form className="signin-form" onSubmit={handleSendOtp} style={{paddingTop:18}}>
            <h2>Verify your phone</h2>
            <ErrorBox/>
            <div className="signin-field">
              <label>Phone Number</label>
              <div style={{display:'flex',alignItems:'center',gap:0}}>
                <div style={{display:'flex',alignItems:'center',gap:5,padding:'11px 12px',background:'#f0f4f8',border:'1.5px solid #e2e8f0',borderRight:'none',borderRadius:'10px 0 0 10px',fontSize:14,fontWeight:600,color:'#4a5568',flexShrink:0}}>
                  <Phone size={14} color="#a0aec0"/>+91
                </div>
                <input type="tel" inputMode="numeric" placeholder="98765 43210" maxLength={10}
                  value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
                  disabled={loading} autoComplete="tel-national"
                  style={{flex:1,padding:'11px 14px',border:'1.5px solid #e2e8f0',borderLeft:'none',borderRadius:'0 10px 10px 0',fontSize:14,fontFamily:'inherit',outline:'none',color:'#1a202c',background:'#f8fafc'}}
                  onFocus={e=>{e.target.style.borderColor=accentColor;e.target.style.background='#fff';}}
                  onBlur={e=>{e.target.style.borderColor='#e2e8f0';e.target.style.background='#f8fafc';}}
                />
              </div>
            </div>
            <button type="submit" className="signin-btn" disabled={loading} style={{background:accentGradient}}>
              {loading?<span className="signin-spinner"/>:<><Phone size={15} style={{marginRight:6}}/>Send OTP</>}
            </button>
          </form>
        )}

        {/* OTP step */}
        {!isAdmin && step === STEP_OTP && (
          <form className="signin-form" onSubmit={handleVerifyOtp} style={{paddingTop:18}}>
            <BackBtn to={STEP_PHONE} label="Change number"/>
            <h2>Enter OTP</h2>
            <p style={{fontSize:13,color:'#64748b',marginBottom:20,marginTop:-10}}>Sent to <strong>{phone}</strong></p>
            <ErrorBox/>
            <div style={{display:'flex',gap:10,justifyContent:'center',marginBottom:22}}>
              {otp.map((digit,idx)=>(
                <input key={idx} ref={el=>otpRefs.current[idx]=el} type="text" inputMode="numeric" maxLength={1}
                  value={digit} onChange={e=>handleOtpChange(e.target.value,idx)}
                  onKeyDown={e=>handleOtpKeyDown(e,idx)} onPaste={handleOtpPaste}
                  style={{width:44,height:52,textAlign:'center',fontSize:22,fontWeight:700,border:`2px solid ${digit?accentColor:'#e2e8f0'}`,borderRadius:12,outline:'none',background:digit?`${accentColor}10`:'#f8fafc',color:'#1c1c1e',fontFamily:'inherit'}}
                />
              ))}
            </div>
            <button type="submit" className="signin-btn" disabled={loading} style={{background:accentGradient}}>
              {loading?<span className="signin-spinner"/>:<><CheckCircle size={15} style={{marginRight:6}}/>Verify OTP</>}
            </button>
            <button type="button" onClick={()=>{setStep(STEP_PHONE);setOtp(['','','','']);setError('');setOtpSent(false);}}
              style={{width:'100%',marginTop:12,padding:'10px',background:'none',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,fontWeight:500,color:'#64748b',cursor:'pointer',fontFamily:'inherit'}}>
              Resend OTP
            </button>
          </form>
        )}

        {/* Credentials step */}
        {(isAdmin || step === STEP_CREDS) && !forgotStep && (
          <form className="signin-form" onSubmit={handleSignIn} style={{paddingTop:18}}>
            {!isAdmin && <BackBtn to={STEP_OTP} label="Back to OTP"/>}
            <h2>Sign in to your account</h2>
            <ErrorBox/>
            <div className="signin-field">
              <label>Username</label>
              <div className="signin-input-wrap">
                <User size={16} className="signin-input-icon"/>
                <input type="text" placeholder={isAdmin?'Enter admin username':'Enter your username'}
                  value={username} onChange={e=>setUsername(e.target.value)}
                  autoComplete="username" disabled={loading}/>
              </div>
            </div>
            <div className="signin-field">
              <label>Password</label>
              <div className="signin-input-wrap">
                <Lock size={16} className="signin-input-icon"/>
                <input type={showPw?'text':'password'} placeholder="Enter your password"
                  value={password} onChange={e=>setPassword(e.target.value)}
                  autoComplete="current-password" disabled={loading}/>
                <button type="button" className="signin-eye" onClick={()=>setShowPw(v=>!v)} tabIndex={-1}>
                  {showPw?<EyeOff size={15}/>:<Eye size={15}/>}
                </button>
              </div>
            </div>
            {isAdmin && (
              <div style={{textAlign:'right',marginTop:-6,marginBottom:14}}>
                <button type="button" onClick={openForgotPassword} style={{
                  background:'none',border:'none',cursor:'pointer',fontFamily:'inherit',padding:0,
                  color:accentColor,fontSize:12,fontWeight:600,
                }}>Forgot password?</button>
              </div>
            )}
            <button type="submit" className="signin-btn" disabled={loading} style={{background:accentGradient}}>
              {loading?<span className="signin-spinner"/>:<><Shield size={15} style={{marginRight:6}}/>Sign In</>}
            </button>
          </form>
        )}

        {/* Forgot password — step 1: enter email */}
        {forgotStep === 'email' && (
          <form className="signin-form" onSubmit={handleForgotRequest} style={{paddingTop:18}}>
            <button type="button" onClick={closeForgotPassword}
              style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'none',cursor:'pointer',color:'#8E8E93',fontSize:12,fontWeight:500,marginBottom:14,padding:0,fontFamily:'inherit'}}>
              <ArrowLeft size={13}/> Back to sign in
            </button>
            <h2>Reset your password</h2>
            <p style={{fontSize:13,color:'#64748b',marginBottom:18,marginTop:-10}}>
              Enter your account email and we'll send you a reset code.
            </p>
            <ErrorBox/>
            {forgotError && <div className="signin-error"><AlertCircle size={15}/><span>{forgotError}</span></div>}
            <div className="signin-field">
              <label>Email</label>
              <div className="signin-input-wrap">
                <User size={16} className="signin-input-icon"/>
                <input type="email" placeholder="you@hoags.in"
                  value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)}
                  autoComplete="email" disabled={forgotLoading}/>
              </div>
            </div>
            <button type="submit" className="signin-btn" disabled={forgotLoading} style={{background:accentGradient}}>
              {forgotLoading?<span className="signin-spinner"/>:'Send Reset Code'}
            </button>
            <button type="button" onClick={closeForgotPassword} style={{
              width:'100%',marginTop:12,padding:'10px',background:'none',border:'1.5px solid #e2e8f0',
              borderRadius:10,fontSize:13,fontWeight:500,color:'#64748b',cursor:'pointer',fontFamily:'inherit',
            }}>Cancel</button>
          </form>
        )}

        {/* Forgot password — step 2: enter code + new password */}
        {forgotStep === 'code' && (
          <form className="signin-form" onSubmit={handleForgotConfirm} style={{paddingTop:18}}>
            <button type="button" onClick={closeForgotPassword}
              style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'none',cursor:'pointer',color:'#8E8E93',fontSize:12,fontWeight:500,marginBottom:14,padding:0,fontFamily:'inherit'}}>
              <ArrowLeft size={13}/> Back to sign in
            </button>
            <h2>Enter reset code</h2>
            <p style={{fontSize:13,color:'#64748b',marginBottom:18,marginTop:-10}}>
              Sent to <strong>{forgotEmail}</strong>
            </p>
            {forgotError && <div className="signin-error"><AlertCircle size={15}/><span>{forgotError}</span></div>}
            <div className="signin-field">
              <label>Reset Code</label>
              <div className="signin-input-wrap">
                <Lock size={16} className="signin-input-icon"/>
                <input type="text" placeholder="123456" maxLength={8}
                  value={forgotCode} onChange={e=>setForgotCode(e.target.value.replace(/\D/g,''))}
                  disabled={forgotLoading}/>
              </div>
            </div>
            <div className="signin-field">
              <label>New Password</label>
              <div className="signin-input-wrap">
                <Lock size={16} className="signin-input-icon"/>
                <input type={showForgotPw?'text':'password'} placeholder="Min 8 chars, upper+lower+digit+special"
                  value={forgotNewPw} onChange={e=>setForgotNewPw(e.target.value)}
                  autoComplete="new-password" disabled={forgotLoading}/>
                <button type="button" className="signin-eye" onClick={()=>setShowForgotPw(v=>!v)} tabIndex={-1}>
                  {showForgotPw?<EyeOff size={15}/>:<Eye size={15}/>}
                </button>
              </div>
            </div>
            <div className="signin-field">
              <label>Confirm New Password</label>
              <div className="signin-input-wrap">
                <Lock size={16} className="signin-input-icon"/>
                <input type={showForgotPw?'text':'password'} placeholder="Re-enter new password"
                  value={forgotConfirmPw} onChange={e=>setForgotConfirmPw(e.target.value)}
                  autoComplete="new-password" disabled={forgotLoading}/>
              </div>
            </div>
            <button type="submit" className="signin-btn" disabled={forgotLoading} style={{background:accentGradient}}>
              {forgotLoading?<span className="signin-spinner"/>:<><CheckCircle size={15} style={{marginRight:6}}/>Reset Password</>}
            </button>
            <button type="button" onClick={()=>{setForgotStep('email'); setForgotError('');}} style={{
              width:'100%',marginTop:12,padding:'10px',background:'none',border:'1.5px solid #e2e8f0',
              borderRadius:10,fontSize:13,fontWeight:500,color:'#64748b',cursor:'pointer',fontFamily:'inherit',
            }}>Resend Code</button>
          </form>
        )}

        {/* Forgot password — done */}
        {forgotStep === 'done' && (
          <div className="signin-form" style={{paddingTop:18,textAlign:'center'}}>
            <CheckCircle size={42} color="#34C759" style={{marginBottom:12}}/>
            <h2>Password Reset!</h2>
            <p style={{fontSize:13,color:'#64748b',marginBottom:20}}>
              You can now sign in with your new password.
            </p>
            <button type="button" onClick={closeForgotPassword} className="signin-btn" style={{background:accentGradient}}>
              Back to Sign In
            </button>
          </div>
        )}

        <div className="signin-footer"><span>Powered by Havells</span></div>
      </div>
    </div>
  );
}
